import { Injectable, inject } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, retry, timeout } from 'rxjs';
import { TarifaRepository } from '../../../domain/repositories/tarifa/tarifa.repository';
import {
  PlanProductCode,
  PlanRateCoverageGroupItem,
  PlanRateCoverageItem,
  PlanRateMoney,
  PlanRateProduct,
  PlanRateResult,
} from '../../../domain/interfaces/tarifa/tarifa.interface';
import { HttpClientService } from '../../services/http-client/http-client.service';
import { API_ENDPOINTS } from '../../config/api.config';

const PLAN_PRODUCT_CODES: readonly PlanProductCode[] = [
  'AUTOS_PlanEsencialDigital',
  'AUTOS_PlanPlusDigital',
  'AUTOS_PlanTotalDigital',
];

const RETRY_COUNT = 2;
const TIMEOUT_MS = 30_000;

interface TarifaRequestDto {
  lead_id: string;
  productCode: PlanProductCode;
}

interface PlanRateMoneyDto {
  currency: string;
  amount: number;
  formatted: string;
}

interface PlanRateProductDto {
  coveragesGroup?: PlanRateCoverageGroupItem[];
  sumInsured?: PlanRateMoneyDto;
  coverages?: PlanRateCoverageItem[];
  order?: number;
  code?: string;
  priceMesConIva?: PlanRateMoneyDto;
  priceMensual?: PlanRateMoneyDto;
  priceAnual?: PlanRateMoneyDto;
  name?: string;
}

interface TarifaApiEnvelope {
  data?: { products?: PlanRateProductDto | PlanRateProductDto[] };
  ExcedePrimaMaximaAnual?: boolean;
  ExcedePrimaMaximaMensual?: boolean;
  Excepcional?: boolean;
  FalloTarifaEsencialAutos?: boolean;
  FalloTarifaTotalAutos?: boolean;
  LimiteMinimoTarifaAutos?: boolean;
  FalloTarifaEsencialMotos?: boolean;
  FalloTarifaTotalMotos?: boolean;
  LimiteMinimoTarifaMotos?: boolean;
  Salvamento?: boolean;
}

@Injectable()
export class TarifaRepositoryImplementation extends TarifaRepository {
  private readonly http = inject(HttpClientService);

  override getAllPlanRates(leadId: string): Observable<PlanRateResult[]> {
    const calls = PLAN_PRODUCT_CODES.map((productCode) =>
      this.http
        .postRaw<TarifaApiEnvelope>(API_ENDPOINTS.tarifas.planes, this.toRequestDto(leadId, productCode))
        .pipe(
          timeout(TIMEOUT_MS),
          retry(RETRY_COUNT),
          map((response) => this.toResult(productCode, response)),
        
          catchError((err: unknown) => {
            console.error(`[tarifas] falló la consulta de ${productCode}:`, err);
            return of(this.toEmptyResult(productCode));
          }),
        ),
    );

    return forkJoin(calls);
  }

  private toRequestDto(leadId: string, productCode: PlanProductCode): TarifaRequestDto {
    return { lead_id: leadId, productCode };
  }

  private toResult(productCode: PlanProductCode, response: TarifaApiEnvelope): PlanRateResult {
    return {
      productCode,
      products: this.toProducts(response.data?.products),
      exceedsMaxPremiumAnnual: response.ExcedePrimaMaximaAnual ?? false,
      exceedsMaxPremiumMonthly: response.ExcedePrimaMaximaMensual ?? false,
      isExceptional: response.Excepcional ?? false,
      failsEssentialAutosRate: response.FalloTarifaEsencialAutos ?? response.FalloTarifaEsencialMotos ?? false,
      failsTotalAutosRate: response.FalloTarifaTotalAutos ?? response.FalloTarifaTotalMotos ?? false,
      belowMinimumAutosRate: response.LimiteMinimoTarifaAutos ?? response.LimiteMinimoTarifaMotos ?? false,
      hasSalvage: response.Salvamento ?? false,
    };
  }

  private toEmptyResult(productCode: PlanProductCode): PlanRateResult {
    return {
      productCode,
      products: [],
      exceedsMaxPremiumAnnual: false,
      exceedsMaxPremiumMonthly: false,
      isExceptional: false,
      failsEssentialAutosRate: false,
      failsTotalAutosRate: false,
      belowMinimumAutosRate: false,
      hasSalvage: false,
    };
  }

  /** Acepta `products` como objeto único (shape real observado en QA) o como array (shape del doc). */
  private toProducts(products: PlanRateProductDto | PlanRateProductDto[] | undefined): PlanRateProduct[] {
    if (!products) return [];
    const list = Array.isArray(products) ? products : [products];
    return list.map((product) => this.toProduct(product));
  }

  private toProduct(product: PlanRateProductDto): PlanRateProduct {
    return {
      coveragesGroup: product.coveragesGroup ?? [],
      sumInsured: this.toMoney(product.sumInsured),
      coverages: product.coverages ?? [],
      order: product.order ?? 0,
      code: product.code ?? '',
      priceMesConIva: this.toMoney(product.priceMesConIva),
      priceMensual: this.toMoney(product.priceMensual),
      priceAnual: this.toMoney(product.priceAnual),
      name: product.name ?? '',
    };
  }

  private toMoney(money: PlanRateMoneyDto | undefined): PlanRateMoney {
    if (!money) return { currency: 'COP', amount: 0, formatted: '' };
    return { currency: money.currency, amount: money.amount, formatted: money.formatted };
  }
}
